import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useAppDispatch } from '@/store';
import { setSidebarOpen } from '@/store/app-slice';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Building2,
  ClipboardCheck,
  Users,
  Settings,
  HelpCircle,
  Home,
  BookOpen,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

type Role = 'super_admin' | 'admin' | 'manager' | 'auditor';

const Logo = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div
      className={`flex items-center px-4 py-6 border-b ${isRTL ? 'flex-row-reverse' : ''}`}
    >
      <div className="flex items-center justify-center w-full">
        <img
          src="/logo%202.png?v=7"
          alt="QPS Logo"
          className="h-[60px] w-auto max-w-[80%] object-contain"
          draggable={false}
        />
      </div>
    </div>
  );
};

const MOBILE_BREAKPOINT = 768; // px - same as app-layout

export default function SideNav() {
  const location = useLocation();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );

  // Track screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle navigation click - close sidebar on mobile
  const handleNavClick = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  // Create navigation items with translations based on 4-role system
  const getNavItems = (role: Role) => {
    const items = {
      // SUPER ADMIN: Full access to all pages
      super_admin: [
        {
          href: '/dashboard',
          icon: LayoutDashboard,
          label: t('navigation.dashboard'),
        },
        {
          href: '/properties',
          icon: Building2,
          label: t('navigation.properties'),
        },
        {
          href: '/assessments',
          icon: ClipboardCheck,
          label: t('navigation.assessments'),
        },
        { href: '/users', icon: Users, label: t('navigation.users') },
        {
          href: '/admin/templates',
          icon: Settings,
          label: t('navigation.templates'),
        },
      ],
      // ADMIN: View all pages (but cannot create/edit/delete except assessments)
      admin: [
        {
          href: '/dashboard',
          icon: LayoutDashboard,
          label: t('navigation.dashboard'),
        },
        {
          href: '/properties',
          icon: Building2,
          label: t('navigation.properties'),
        },
        {
          href: '/assessments',
          icon: ClipboardCheck,
          label: t('navigation.assessments'),
        },
        { href: '/users', icon: Users, label: t('navigation.users') },
        {
          href: '/admin/templates',
          icon: Settings,
          label: t('navigation.templates'),
        },
      ],
      // MANAGER: Only see their property detail page
      manager: [
        {
          href: `/properties/${user?.property?.id || ''}`,
          icon: Home,
          label: t('navigation.myProperty') || 'My Property',
        },
      ],
      // AUDITOR: Only see their assigned assessments
      auditor: [
        {
          href: '/assessments',
          icon: ClipboardCheck,
          label: t('navigation.myAssessments') || 'My Assessments',
        },
      ],
    };
    return items[role] || [];
  };

  const currentNavItems = user
    ? getNavItems((user.roles?.[0] || 'auditor') as Role)
    : [];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    // Handle property detail page for manager
    if (path.startsWith('/properties/') && location.pathname.startsWith('/properties/')) {
      return true;
    }
    return location.pathname.startsWith(path);
  };

  if (!user) return null;

  return (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      <Logo />

      {/* Navigation Items */}
      <nav className="flex-1 px-3 space-y-1 py-4">
        {currentNavItems.map(item => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <Button
              key={item.href}
              variant={active ? 'secondary' : 'ghost'}
              className={cn(
                'w-full h-12 px-4',
                isRTL
                  ? 'justify-end text-right flex-row-reverse'
                  : 'justify-start text-left',
                active && 'bg-sidebar-accent text-sidebar-accent-foreground',
                !active &&
                  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              )}
              asChild
            >
              <Link
                to={item.href}
                className="flex items-center"
                onClick={handleNavClick}
              >
                <Icon className={cn('h-5 w-5', isRTL ? 'ml-3' : 'mr-3')} />
                <span className="truncate">{item.label}</span>
              </Link>
            </Button>
          );
        })}
      </nav>

      {/* Guidelines Section - Above Support Team */}
      <div className="px-3 pb-3">
        <Button
          variant={isActive('/guidelines') ? 'secondary' : 'ghost'}
          className={cn(
            'w-full h-12 px-4',
            isRTL
              ? 'justify-end text-right flex-row-reverse'
              : 'justify-start text-left',
            isActive('/guidelines') && 'bg-sidebar-accent text-sidebar-accent-foreground',
            !isActive('/guidelines') &&
              'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
          )}
          asChild
        >
          <Link
            to="/guidelines"
            className="flex items-center"
            onClick={handleNavClick}
          >
            <BookOpen className={cn('h-5 w-5', isRTL ? 'ml-3' : 'mr-3')} />
            <span className="truncate">{t('navigation.guidelines') || 'Guidelines'}</span>
          </Link>
        </Button>
      </div>

      {/* E-Ticket Section - Support Team */}
      <div className="px-3 space-y-3">
        <a
          href="mailto:support@codecircle.co"
          className={cn(
            'flex items-center gap-2 px-2 py-2.5 rounded-md transition-colors cursor-pointer hover:bg-sidebar-accent'
          )}
          dir="ltr"
        >
          <HelpCircle className="h-5 w-5 text-white shrink-0" />
          <div className="flex flex-col overflow-hidden">
            <span
              className={cn(
                'text-xs font-medium text-sidebar-foreground truncate'
              )}
            >
              {t('supportTeam')}
            </span>
            <span className="text-[10px] text-muted-foreground truncate opacity-70">
              support@codecircle.co
            </span>
          </div>
        </a>
      </div>

      {/* Footer */}
      <div className="px-3 pb-4 space-y-3 border-t pt-4">
        {/* User Profile */}
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2',
            isRTL ? 'flex-row-reverse' : ''
          )}
        >
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="text-xs text-[hsl(218,64%,10%)]">
              {user.name
                ?.split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div
            className={cn(
              'flex flex-col text-xs overflow-hidden',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            <span className="font-semibold text-sidebar-foreground truncate leading-tight">
              {user.name}
            </span>
            <span className="text-xs text-muted-foreground truncate opacity-70">
              {user.email}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
